import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.tsx';
import { gql } from '@/__generated__/index.ts';

const SUBSCRIBE_TO_USER_POSTS = gql(`
  mutation SubscribeToUserPosts($userId: ID!) {
    subscribeToUserPosts(userId: $userId)
  }
`);

const UNSUBSCRIBE_FROM_USER_POSTS = gql(`
  mutation UnsubscribeFromUserPosts($userId: ID!) {
    unsubscribeFromUserPosts(userId: $userId)
  }
`);

export const useSubscribeToUserPosts = (userId: string, username: string) => {
  const toast = useToast();

  const [subscribeToUserPostsMutation, result] = useMutation(
    SUBSCRIBE_TO_USER_POSTS,
    {
      onCompleted() {
        toast.info({
          title: `Successfully subscribed to ${username}'s posts`,
        });
      },
      onError() {
        toast.error({
          title: `Failed to subscribe to ${username}'s posts`,
          subTitle: 'Please try again',
        });
      },

      update(cache) {
        cache.modify({
          id: cache.identify({
            __typename: 'User',
            id: userId,
          }),
          fields: {
            subscribedToUserPosts() {
              return true;
            },
          },
        });
      },
    },
  );

  const subscribeToUserPosts = async () => {
    await subscribeToUserPostsMutation({
      variables: {
        userId,
      },
      optimisticResponse: {
        subscribeToUserPosts: true,
      },
    });
  };

  return [subscribeToUserPosts, result] as const;
};

export const useUnsubscribeFromUserPosts = (
  userId: string,
  username: string,
) => {
  const toast = useToast();

  const [unsubscribeFromUserPostsMutation, result] = useMutation(
    UNSUBSCRIBE_FROM_USER_POSTS,
    {
      onCompleted() {
        toast.info({
          title: `Successfully unsubscribed from ${username}'s posts`,
        });
      },
      onError() {
        toast.error({
          title: `Failed to unsubscribe from ${username}'s posts`,
          subTitle: 'Please try again',
        });
      },

      update(cache) {
        cache.modify({
          id: cache.identify({
            __typename: 'User',
            id: userId,
          }),
          fields: {
            subscribedToUserPosts() {
              return false;
            },
          },
        });
      },
    },
  );

  const unsubscribeFromUserPosts = async () => {
    await unsubscribeFromUserPostsMutation({
      variables: {
        userId,
      },
      optimisticResponse: {
        unsubscribeFromUserPosts: true,
      },
    });
  };

  return [unsubscribeFromUserPosts, result] as const;
};
